import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWebSocket } from '../../context/WebSocketContext';
import {
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BellIcon,
  ChartBarIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const ActivityFeed = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const { socket, isConnected } = useWebSocket();

  useEffect(() => {
    // Load initial activities
    loadActivities();
    
    // Listen for real-time activities
    if (socket && isConnected) {
      socket.on('activity_update', handleActivityUpdate);
      socket.on('leave_update', handleLeaveUpdate);
      socket.on('user_activity', handleUserActivity);
    }

    return () => {
      if (socket) {
        socket.off('activity_update', handleActivityUpdate);
        socket.off('leave_update', handleLeaveUpdate);
        socket.off('user_activity', handleUserActivity);
      }
    };
  }, [socket, isConnected]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      // In a real app, you'd fetch activities from an API
      // For now, we'll simulate with mock data
      const mockActivities = [
        {
          id: 1,
          type: 'leave_request',
          user: 'John Doe',
          action: 'submitted',
          details: 'Annual Leave - 5 days',
          timestamp: new Date(Date.now() - 5 * 60 * 1000),
          icon: CalendarDaysIcon,
          color: 'blue'
        },
        {
          id: 2,
          type: 'leave_approval',
          user: 'HR Manager',
          action: 'approved',
          details: 'Sick Leave - 2 days',
          timestamp: new Date(Date.now() - 15 * 60 * 1000),
          icon: CheckCircleIcon,
          color: 'green'
        },
        {
          id: 3,
          type: 'system',
          user: 'System',
          action: 'notification',
          details: 'Daily report generated',
          timestamp: new Date(Date.now() - 30 * 60 * 1000),
          icon: BellIcon,
          color: 'purple'
        }
      ];
      
      setActivities(mockActivities);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityUpdate = (data) => {
    const newActivity = {
      id: Date.now(),
      type: data.type,
      user: data.user || 'System',
      action: data.action,
      details: data.details,
      timestamp: new Date(),
      icon: getActivityIcon(data.type),
      color: getActivityColor(data.type)
    };
    
    setActivities(prev => [newActivity, ...prev.slice(0, 19)]); // Keep last 20 activities
  };

  const handleLeaveUpdate = (data) => {
    const activityMap = {
      'new_request': { action: 'submitted', details: 'Leave Request' },
      'status_change': { action: data.data.status === 'approved' ? 'approved' : 'rejected', details: 'Leave Request' },
      'bulk_status_change': { action: `bulk ${data.data.action}`, details: `${data.data.processedCount} requests` }
    };

    if (activityMap[data.type]) {
      const { action, details } = activityMap[data.type];
      const newActivity = {
        id: Date.now(),
        type: 'leave_request',
        user: 'Employee',
        action,
        details,
        timestamp: new Date(),
        icon: CalendarDaysIcon,
        color: 'blue'
      };
      
      setActivities(prev => [newActivity, ...prev.slice(0, 19)]);
    }
  };

  const handleUserActivity = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'user_activity',
      user: data.user,
      action: data.action,
      details: data.details,
      timestamp: new Date(),
      icon: UserIcon,
      color: 'gray'
    };
    
    setActivities(prev => [newActivity, ...prev.slice(0, 19)]);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'leave_request':
        return CalendarDaysIcon;
      case 'leave_approval':
        return CheckCircleIcon;
      case 'system':
        return CogIcon;
      case 'notification':
        return BellIcon;
      case 'report':
        return ChartBarIcon;
      default:
        return ClockIcon;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'leave_request':
        return 'blue';
      case 'leave_approval':
        return 'green';
      case 'leave_rejection':
        return 'red';
      case 'system':
        return 'purple';
      case 'notification':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      purple: 'bg-purple-100 text-purple-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    return colorMap[color] || colorMap.gray;
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="relative">
      {/* Activity Feed Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
      >
        <ClockIcon className="h-5 w-5" />
        {activities.length > 0 && (
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-blue-500 rounded-full animate-pulse"></span>
        )}
      </button>

      {/* Activity Feed Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden"
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Live Activity Feed</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Real-time system activities and updates
              </p>
            </div>

            <div className="overflow-y-auto max-h-80">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading activities...</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="p-4 text-center">
                  <ClockIcon className="h-8 w-8 text-gray-400 mx-auto" />
                  <p className="text-sm text-gray-500 mt-2">No recent activities</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {activities.map((activity) => {
                    const IconComponent = activity.icon;
                    return (
                      <motion.div
                        key={activity.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`flex-shrink-0 p-2 rounded-full ${getColorClasses(activity.color)}`}>
                            <IconComponent className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">
                                {activity.user}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatTimeAgo(activity.timestamp)}
                              </p>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {activity.action} {activity.details}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {activities.length > 0 && (
              <div className="p-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{activities.length} recent activities</span>
                  <button
                    onClick={loadActivities}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityFeed; 